// Client wrapper over the `suggest-canonical` edge function.
//
// Verify-or-propose pass: given the OFF/USDA payload from a UPC scan
// AND the registry's programmatic guess (if any), Haiku decides
// whether to accept the guess or propose a more specific canonical.
//
// Why this matters: the bundled `inferCanonicalFromName` is a word
// matcher. It will happily bind "Ski Queen Gjetost" to the generic
// `cheese` canonical because Gjetost is unmapped — Haiku reads the
// full context (brand + name + ingredients + categories) and
// recognizes Gjetost as a distinct cheese variety that deserves its
// own canonical.
//
// Pure inference — no DB writes, no notifications, no pending row.
// Use this for the SCAN-REVIEW PROPOSAL step. After the user
// confirms, mint the canonical via createPendingCanonicalFromScan
// or enrichIngredient.
//
// On failure, throws with a user-readable message. Callers should
// catch and fall back to the tentative match (if present) or to a
// programmatic guess so the proposal popup is never empty.

import { supabase } from "./supabase";

/**
 * Ask Haiku to verify or propose a canonical for a scanned product.
 *
 * @param {object} input
 * @param {string} input.productName - OFF productName / USDA description
 * @param {string|null} [input.brand] - single brand value; ignored if brandCandidates is set
 * @param {{value:string,source:"off"|"usda"|"correction"|"scan"}[]|null} [input.brandCandidates]
 *   Multiple brand candidates from the OFF/USDA/correction merge.
 *   When more than one is present, Haiku picks (e.g. Tine over Norseland Inc.).
 * @param {string[]|null} [input.categoryHints]
 * @param {string|null} [input.ingredientsText]
 * @param {string|null} [input.barcodeUpc]
 * @param {{ canonicalId: string, name: string }|null} [input.tentativeMatch]
 *   The registry's programmatic guess (from inferCanonicalFromName) if any.
 *   Haiku verifies it; if too generic, returns a propose decision.
 * @param {{ id: string, name: string, category?: string, parentHub?: string }[]|null} [input.registeredHubs]
 *   Bundled HUBS so Haiku picks slugs from the registered list when they fit
 *   (avoids inventing slugs that don't resolve client-side).
 * @param {{ fridge?: {id,label}[], pantry?: {id,label}[], freezer?: {id,label}[] }|null} [input.registeredTiles]
 *   FRIDGE/PANTRY/FREEZER_TILES so Haiku's tileLabel lands on a real tile id.
 * @returns {Promise<
 *   | { decision: "accept", canonicalId: string, reasoning: string|null,
 *       brand: { name: string, reasoning: string|null }|null }
 *   | { decision: "propose", suggestion: {
 *       name: string,
 *       slug: string,
 *       confidence: "high"|"medium"|"low",
 *       reasoning: string|null,
 *       claims: string[],
 *       variant: string|null,
 *       category: string|null,
 *       subtype: string|null,
 *       location: "fridge"|"pantry"|"freezer"|null,
 *       tileLabel: string|null,
 *       state: string|null,
 *       hubChain: string[],
 *       newHubs: { id: string, name: string, category: string|null, parentHub: string|null }[],
 *       parentHub: string|null,
 *       packageSizeOptions: { amount: number, unit: string }[],
 *       brand: { name: string, reasoning: string|null }|null,
 *       supersedesTentative: boolean,
 *     } }
 * >}
 */
export async function suggestCanonical({
  productName = null,
  brand = null,
  brandCandidates = null,
  categoryHints = null,
  ingredientsText = null,
  barcodeUpc = null,
  tentativeMatch = null,
  registeredHubs = null,
  registeredTiles = null,
} = {}) {
  if (!productName?.trim() && !ingredientsText?.trim()) {
    throw new Error("suggestCanonical: productName or ingredientsText is required");
  }

  const body = {
    productName, brand, brandCandidates, categoryHints, ingredientsText, barcodeUpc, tentativeMatch,
    registeredHubs, registeredTiles,
  };

  const { data, error } = await supabase.functions.invoke("suggest-canonical", { body });

  if (error) {
    let detail = "";
    if (error.context?.body) {
      try {
        const parsed = typeof error.context.body === "string"
          ? JSON.parse(error.context.body)
          : error.context.body;
        detail = parsed?.detail || parsed?.error || "";
      } catch {
        // ignore
      }
    }
    const msg = detail
      ? `Suggestion failed: ${detail}`
      : `Suggestion failed: ${error.message || "unknown error"}`;
    throw new Error(msg);
  }

  if (!data?.decision) {
    throw new Error("Suggestion returned no decision");
  }
  if (data.decision === "accept" && !data.canonicalId) {
    throw new Error("Accept decision missing canonicalId");
  }
  if (data.decision === "propose" && !data?.suggestion?.name) {
    throw new Error("Propose decision missing suggestion.name");
  }

  return data;
}
